import { AnyAction } from 'redux';
import ActionTypes from '~/constants/actionTypes';
import AvailabilityError from '~/constants/availabilityError';
import SoftPullStatus from '~/constants/softPullStatus';
import Store from '~/constants/store';
import type {
  AvailabilityResponse,
  ReduxStore,
  SpinnerType,
  User,
  UserFinancing,
  Vehicle
} from '~/contracts/vdp';
import initialState from '~/store/initialState';
import * as types from './actionTypes';

const filterHighlights = (highlights = []) => {
  const ignoreTags = Object.values(Store); // TODO: Filter these server-side

  return highlights.filter(({ tagKey }) => !ignoreTags.includes(tagKey));
};

export function vehicle(
  state: Vehicle = initialState.vehicle,
  action: AnyAction = { type: '' }
): Vehicle {
  const { error, payload, type } = action;

  switch (type) {
    case types.GET_VEHICLE_DETAILS: {
      return {
        ...state,
        details: {
          ...state.details,
          ...payload,
          highlights: filterHighlights(payload.highlights)
        },
        isDetailsLoaded: true
      };
    }
    case types.SPINNER_FAILED_LOADING:
      return {
        ...state,
        spinner: {
          ...state.spinner,
          hasError: payload
        }
      };
    case types.SET_SPINNER_SCRIPT_INSERTED:
      return {
        ...state,
        spinner: {
          ...state.spinner,
          isScriptInserted: true
        }
      };
    case types.SET_SPINNER_IMMERSIVE:
      return {
        ...state,
        spinner: {
          ...state.spinner,
          isImmersive: payload
        }
      };
    case types.SET_SPINNER_TOUR_MODE:
      return {
        ...state,
        spinner: {
          ...state.spinner,
          inTourMode: payload
        }
      };
    case types.SET_SPINNER_TYPE:
      return {
        ...state,
        spinner: {
          ...state.spinner,
          type: payload as SpinnerType
        }
      };
    case `${types.GET_RECALL_STATUS}_LOADING`:
      return {
        ...state,
        alertModals: {
          ...state.alertModals,
          isLoading: true
        }
      };
    case `${types.GET_RECALL_STATUS}_SUCCESS`:
      return {
        ...state,
        alertModals: {
          ...state.alertModals,
          isLoading: false,
          recallStatus: [...payload]
        }
      };
    case `${types.GET_RECALL_STATUS}_ERROR`:
      return {
        ...state,
        alertModals: {
          ...state.alertModals,
          isLoading: false,
          error: payload,
          isError: error
        }
      };

    case `${types.GET_DELIVERY_PICKUP_DATA}_LOADING`:
      return {
        ...state,
        deliveryPickup: {
          ...state.deliveryPickup,
          isLoaded: false,
          isLoading: true
        }
      };
    case `${types.GET_DELIVERY_PICKUP_DATA}_SUCCESS`:
      return {
        ...state,
        deliveryPickup: {
          data: payload,
          isLoaded: true,
          isLoading: false,
          hasError: false
        }
      };
    case `${types.GET_DELIVERY_PICKUP_DATA}_ERROR`:
      return {
        ...state,
        deliveryPickup: {
          data: {
            soonestDelivery: {},
            soonestPickup: {},
            vehicleId: 0
          },
          error: payload,
          isLoaded: true,
          isLoading: false,
          hasError: action.error
        }
      };
    case types.REMOVE_SOONEST_DELIVERY:
      return {
        ...state,
        deliveryPickup: {
          ...state.deliveryPickup,
          data: {
            soonestDelivery: {},
            soonestPickup: { ...state.deliveryPickup.data.soonestPickup },
            vehicleId: state.deliveryPickup.data.vehicleId
          }
        }
      };

    case `${types.GET_TRANSIT_CHARGE}_LOADING`:
      return {
        ...state,
        transitCharge: {
          ...state.transitCharge,
          isLoaded: false,
          isLoading: true
        }
      };
    case `${types.GET_TRANSIT_CHARGE}_SUCCESS`:
      return {
        ...state,
        transitCharge: {
          ...state.transitCharge,
          data: payload as number,
          hasError: false,
          isLoaded: true,
          isLoading: false
        }
      };
    case `${types.GET_TRANSIT_CHARGE}_ERROR`:
      return {
        ...state,
        transitCharge: {
          data: -1,
          error: action.error,
          hasError: true,
          isLoaded: true,
          isLoading: false
        }
      };

    case types.GET_TRANSIT_ROUTE:
      return {
        ...state,
        ...payload
      };

    case `${types.GET_HAS_OWNER_REVIEWS}_SUCCESS`:
      return {
        ...state,
        hasOwnerReviews: payload
      };
    case types.SET_CURRENT_VEHICLE_FAVORITED:
      return {
        ...state,
        isFavorited: payload.isFavorited,
        isSubscribedToNotifications:
          payload.isSubscribedToNotifications ?? false
      };
    case types.SET_ANALYTICS_EVENT_IDS:
      return {
        ...state,
        analytics: payload
      };
    default:
      return state;
  }
}

export function experimentation(
  state: ReduxStore['experimentation'] = initialState.experimentation,
  action: AnyAction = { type: '' }
): ReduxStore['experimentation'] {
  const { type, payload } = action;

  switch (type) {
    case types.ADD_EXPERIMENT: {
      return {
        ...state,
        experiments: {
          ...state.experiments,
          ...payload
        }
      };
    }
    case types.SET_EXPERIMENTS_LOADED: {
      return {
        ...state,
        ...payload
      };
    }
    default:
      return state;
  }
}

export function user(
  state: User = initialState.user,
  action: AnyAction = { type: '' }
): User {
  const { error, payload, type } = action;

  switch (type) {
    case ActionTypes.setUserId:
      return { ...state, userId: payload };

    case `${types.GET_USER_PROFILE}_ERROR`:
      return {
        ...state,
        error: payload.message,
        isLoaded: true,
        isLoading: false
      };
    case `${types.GET_USER_PROFILE}_LOADING`:
      return {
        ...state,
        isLoading: true
      };
    case `${types.GET_USER_PROFILE}_SUCCESS`:
      return {
        ...state,
        ...payload,
        isLoaded: true,
        isLoading: false
      };

    case `${types.GET_USER_AVAILABILITY}_ERROR`:
      return {
        ...state,
        availability: {
          ...state.availability,
          isError: error,
          error: payload.message,
          isLoaded: true,
          isLoading: false
        }
      };
    case `${types.GET_USER_AVAILABILITY}_LOADING`:
      return {
        ...state,
        availability: {
          ...state.availability,
          isLoading: true
        }
      };
    case types.GET_USER_AVAILABILITY:
    case `${types.GET_USER_AVAILABILITY}_SUCCESS`: {
      const typedPayload = payload as AvailabilityResponse;
      const isPendingDealerPurchase = Boolean(
        typedPayload.dealerPurchaseInitiatedByUserId &&
          typedPayload.isDealerPurchased === null
      );
      const isPendingDealerPurchaseByUser = Boolean(
        isPendingDealerPurchase &&
          typedPayload.dealerPurchaseInitiatedByUserId === state.userId
      );

      return {
        ...state,
        availability: {
          ...typedPayload,
          // NOTE We can remove this if/when the API treats pending purchases as `PurchasePending`
          error:
            typedPayload.error === AvailabilityError.None &&
            isPendingDealerPurchase &&
            !isPendingDealerPurchaseByUser
              ? AvailabilityError.PurchasePending
              : typedPayload.error || 'None',
          isPendingDealerPurchase,
          isPendingDealerPurchaseByUser,
          isError: false,
          isLoaded: true,
          isLoading: false
        }
      };
    }
    case `${types.GET_USER_PURCHASING}_SUCCESS`:
      return {
        ...state,
        purchasing: payload
      };

    case `${types.GET_USER_FINANCING}_ERROR`:
      return {
        ...state,
        financing: {
          data: state.financing.data,
          error: payload,
          hasError: true,
          isLoaded: true,
          isLoading: false
        }
      };
    case `${types.GET_USER_FINANCING}_LOADING`:
      return {
        ...state,
        financing: {
          ...state.financing,
          isLoaded: false,
          isLoading: true
        }
      };
    case `${types.GET_USER_FINANCING}_SUCCESS`: {
      const getSoftPullStatus = (
        isValid: boolean | undefined
      ): SoftPullStatus => {
        if (typeof isValid === 'undefined') {
          return SoftPullStatus.None;
        }

        return isValid ? SoftPullStatus.Active : SoftPullStatus.Expired;
      };

      return {
        ...state,
        financing: {
          data: payload ?? null,
          error: undefined,
          hasError: false,
          isLoaded: true,
          isLoading: false
        },
        softPullStatus: getSoftPullStatus(
          (payload as UserFinancing | null)?.isValid
        )
      };
    }
    case ActionTypes.setGeolocation:
      return {
        ...state,
        location: {
          ...state.location,
          ...payload.location,
          city: payload.city || '',
          state: payload.state || '',
          zip: payload.zip || ''
        }
      };

    case `${types.ADD_FAVORITE}_LOADING`:
    case `${types.GET_FAVORITES}_LOADING`:
    case `${types.REMOVE_FAVORITE}_LOADING`:
      return {
        ...state,
        favorites: {
          ...state.favorites,
          isLoading: true
        }
      };
    case `${types.GET_FAVORITES}_SUCCESS`:
      return {
        ...state,
        favorites: {
          ...state.favorites,
          isLoading: false,
          isLoaded: true,
          vehicles: [...payload.vehicles]
        }
      };
    case `${types.GET_FAVORITES}_ERROR`:
      return {
        ...state,
        favorites: {
          isError: error,
          error: payload,
          isLoaded: true,
          isLoading: false,
          vehicles: []
        }
      };
    case `${types.ADD_FAVORITE}_SUCCESS`:
    case `${types.REMOVE_FAVORITE}_SUCCESS`:
      return {
        ...state,
        favorites: {
          ...state.favorites
        }
      };
    case `${types.ADD_FAVORITE}_ERROR`:
    case `${types.REMOVE_FAVORITE}_ERROR`:
      return {
        ...state,
        favorites: {
          ...state.favorites,
          isError: error,
          error: payload,
          isLoading: false
        }
      };
    case ActionTypes.setReferrer:
      return {
        ...state,
        referrer: payload
      };
    default:
      return state;
  }
}

export function router(
  state: ReduxStore['router'] = initialState.router,
  action: AnyAction = { type: '' }
) {
  const { type, payload } = action;

  switch (type) {
    case ActionTypes.setQueryParams:
      return {
        ...state,
        queryParams: payload
      };
    case types.SET_HASH:
      return {
        ...state,
        hash: payload
      };
    default:
      return state;
  }
}
